import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaSchool } from 'react-icons/fa';

function SchoolFilterOption({ name, onSelect }) {
    return (
        <button
            type="button"
            role="option"
            className="rich-dropdown__option"
            onClick={() => onSelect(name)}
        >
            <span
                className="rich-dropdown__option-icon"
                style={{ color: '#1d4ed8', backgroundColor: '#dbeafe' }}
            >
                <FaSchool aria-hidden="true" />
            </span>
            <span className="rich-dropdown__option-body">
                <span className="rich-dropdown__option-label">{name}</span>
                <span className="rich-dropdown__option-hint">Tap to add school</span>
            </span>
            <span className="rich-dropdown__option-mark order-status-filter__option-add" aria-hidden="true">
                <FaPlus />
            </span>
        </button>
    );
}

function SchoolFilterChip({ name, onRemove }) {
    return (
        <span
            className="order-status-filter__chip school-filter__chip"
            style={{
                color: '#1d4ed8',
                backgroundColor: '#dbeafe',
                borderColor: '#1d4ed8',
            }}
        >
            <FaSchool className="order-status-filter__chip-icon" aria-hidden="true" />
            <span className="order-status-filter__chip-label">{name}</span>
            <button
                type="button"
                className="order-status-filter__chip-remove"
                onClick={onRemove}
                aria-label={`Remove ${name}`}
            >
                ×
            </button>
        </span>
    );
}

export default function SchoolFilterDropdown({
    panelId,
    schoolOptions,
    schoolSelections,
    onToggleSchool,
    placeholder = 'Search schools...',
}) {
    const [open, setOpen] = useState(false);
    const [schoolSearch, setSchoolSearch] = useState('');
    const searchWrapRef = useRef(null);
    const inputRef = useRef(null);
    const panelRef = useRef(null);
    const [panelStyle, setPanelStyle] = useState({
        position: 'fixed',
        top: 0,
        left: 0,
        width: 200,
        zIndex: 10050,
        visibility: 'hidden',
    });

    const selectedSchools = schoolOptions.filter((name) => schoolSelections[name]);
    const unselectedSchools = schoolOptions.filter((name) => !schoolSelections[name]);

    const searchNorm = schoolSearch.trim().toLowerCase();
    const visibleSchools = unselectedSchools.filter(
        (name) => !searchNorm || name.toLowerCase().includes(searchNorm)
    );

    const updatePosition = useCallback(() => {
        const el = searchWrapRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const pad = 8;
        const width = Math.max(Math.round(r.width), 220);
        let left = r.left;
        if (left + width > window.innerWidth - pad) {
            left = Math.max(pad, window.innerWidth - width - pad);
        }
        const maxPanelH = 320;
        let top = r.bottom + 4;
        if (top + maxPanelH > window.innerHeight - pad) {
            top = Math.max(pad, r.top - maxPanelH - 4);
        }
        setPanelStyle({
            position: 'fixed',
            left,
            top,
            width,
            zIndex: 10050,
            visibility: 'visible',
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        const onResizeOrScroll = () => updatePosition();
        window.addEventListener('resize', onResizeOrScroll);
        window.addEventListener('scroll', onResizeOrScroll, true);
        return () => {
            window.removeEventListener('resize', onResizeOrScroll);
            window.removeEventListener('scroll', onResizeOrScroll, true);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            const t = e.target;
            if (!(t instanceof Node)) return;
            if (searchWrapRef.current?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            setOpen(false);
            setSchoolSearch('');
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                setSchoolSearch('');
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    const handleSelect = (name) => {
        if (!schoolSelections[name]) {
            onToggleSchool(name);
        }
        setSchoolSearch('');
        inputRef.current?.focus();
    };

    const panel =
        open &&
        createPortal(
            <div
                ref={panelRef}
                id={panelId}
                className="jreats-order-filter__panel jreats-order-filter__panel--floating rich-dropdown__panel order-status-filter__panel"
                style={panelStyle}
                role="listbox"
                aria-label="Available schools"
            >
                {schoolOptions.length === 0 ? (
                    <p className="jreats-order-filter__empty">No schools in current orders</p>
                ) : visibleSchools.length > 0 ? (
                    <>
                        <div className="rich-dropdown__panel-header">
                            <span className="rich-dropdown__panel-title">Add school</span>
                            <span className="rich-dropdown__panel-count">
                                {visibleSchools.length} available
                            </span>
                        </div>
                        <div className="rich-dropdown__panel-body">
                            {visibleSchools.map((name) => (
                                <SchoolFilterOption key={name} name={name} onSelect={handleSelect} />
                            ))}
                        </div>
                    </>
                ) : (
                    <p className="jreats-order-filter__empty">
                        {unselectedSchools.length === 0
                            ? 'All schools selected'
                            : 'No schools found'}
                    </p>
                )}
            </div>,
            document.body
        );

    return (
        <div className="order-status-filter school-filter">
            <div
                className={`order-status-filter__search-box${open ? ' order-status-filter__search-box--open' : ''}`}
                ref={searchWrapRef}
            >
                <input
                    ref={inputRef}
                    type="search"
                    className="order-status-filter__search-input"
                    placeholder={placeholder}
                    value={schoolSearch}
                    onChange={(e) => {
                        setSchoolSearch(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    aria-expanded={open}
                    aria-controls={panelId}
                    aria-label="Search and add schools"
                    autoComplete="off"
                />
            </div>
            {panel}
            {selectedSchools.length > 0 ? (
                <div className="order-status-filter__chips" aria-label="Selected schools">
                    {selectedSchools.map((name) => (
                        <SchoolFilterChip
                            key={name}
                            name={name}
                            onRemove={() => onToggleSchool(name)}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}
